// File validation utilities for the attachment system

import { FileValidationResult, ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '../../types/file-attachments';

export interface SecurityValidationOptions {
  allowExecutables: boolean;
  scanForMalware: boolean;
  checkFileHeaders: boolean;
  maxDepthZip: number;
}

export const DEFAULT_SECURITY_OPTIONS: SecurityValidationOptions = {
  allowExecutables: false,
  scanForMalware: true,
  checkFileHeaders: true,
  maxDepthZip: 3
};

// File signature patterns for header validation
const FILE_SIGNATURES: Record<string, number[][]> = {
  'image/jpeg': [[0xFF, 0xD8, 0xFF]],
  'image/png': [[0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]],
  'image/gif': [[0x47, 0x49, 0x46, 0x38, 0x37, 0x61], [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]],
  'application/pdf': [[0x25, 0x50, 0x44, 0x46]],
  'application/zip': [[0x50, 0x4B, 0x03, 0x04], [0x50, 0x4B, 0x05, 0x06], [0x50, 0x4B, 0x07, 0x08]]
};

// Dangerous file extensions that should never be allowed
const DANGEROUS_EXTENSIONS = [
  '.exe', '.scr', '.bat', '.cmd', '.com', '.pif', '.scf', '.vbs', '.js', '.jse',
  '.jar', '.msi', '.reg', '.ps1', '.psm1', '.psd1', '.psc1', '.msc', '.inf',
  '.hta', '.cpl', '.mst', '.application', '.gadget', '.msp', '.ins', '.isp',
  '.app', '.dmg', '.pkg', '.deb', '.rpm', '.sh', '.bash', '.htm', '.html',
  '.php', '.jsp', '.asp', '.aspx', '.cgi', '.pl', '.py', '.rb', '.dll',
  '.so', '.dylib', '.ocx', '.sys', '.drv', '.vxd', '.ws', '.wsf', '.wsh',
  '.wsc', '.lnk', '.url', '.website', '.job', '.vb', '.vbe', '.mde', '.mdb',
  '.mda', '.mdw', '.mdz', '.mdt', '.ops', '.prf', '.prg', '.pst', '.reg',
  '.scf', '.sct', '.shb', '.shs', '.vsmacros', '.ws', '.xll', '.xnk'
];

export function validateFile(file: File, options: Partial<SecurityValidationOptions> = {}): Promise<FileValidationResult> {
  return new Promise((resolve) => {
    const opts = { ...DEFAULT_SECURITY_OPTIONS, ...options };
    const errors: string[] = [];
    const warnings: string[] = [];

    // Basic validation
    validateBasicProperties(file, errors, warnings);
    
    // Security validation
    validateSecurity(file, errors, warnings, opts);

    // If we need to check file headers, read the file
    if (opts.checkFileHeaders && errors.length === 0) {
      // First validate file headers
      validateFileHeaders(file).then(async (headerResult) => {
        if (!headerResult.isValid) {
          errors.push(...headerResult.errors);
          warnings.push(...headerResult.warnings);
        }
        
        // Then validate file content for malicious patterns
        if (errors.length === 0) {
          const contentResult = await validateFileContent(file);
          if (!contentResult.isValid) {
            errors.push(...contentResult.errors);
            warnings.push(...contentResult.warnings);
          }
        }
        
        resolve({
          isValid: errors.length === 0,
          errors,
          warnings
        });
      }).catch(() => {
        warnings.push('Could not validate file headers');
        resolve({
          isValid: errors.length === 0,
          errors,
          warnings
        });
      });
    } else {
      resolve({
        isValid: errors.length === 0,
        errors,
        warnings
      });
    }
  });
}

function validateBasicProperties(file: File, errors: string[], warnings: string[]): void {
  // File size validation
  if (file.size === 0) {
    errors.push('File is empty');
    return;
  }

  if (file.size > MAX_FILE_SIZE) {
    errors.push(`File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(MAX_FILE_SIZE)})`);
  }

  // File type validation
  if (!file.type) {
    warnings.push('File type could not be determined');
  } else if (!ALLOWED_FILE_TYPES.all.includes(file.type)) {
    errors.push(`File type "${file.type}" is not allowed`);
  }

  // File name validation
  if (!file.name || file.name.trim().length === 0) {
    errors.push('File name is required');
    return;
  }

  if (file.name.length > 255) {
    errors.push('File name is too long (maximum 255 characters)');
  }

  // Check for null bytes or other suspicious characters
  if (file.name.includes('\0') || file.name.includes('\x00')) {
    errors.push('File name contains invalid characters');
  }

  // Enhanced path traversal protection
  const pathTraversalPatterns = [
    /\.\./,                    // Basic traversal
    /\.\.\\/,                  // Windows traversal
    /%2e%2e/i,                 // URL encoded
    /%252e%252e/i,             // Double encoded
    /\x00/,                    // Null byte
    /[\x01-\x1f\x7f-\x9f]/,   // Control characters
    /\/{2,}/,                  // Multiple slashes
    /\\/,                      // Backslash
    /\0/,                      // Null character
  ];
  
  if (pathTraversalPatterns.some(pattern => pattern.test(file.name))) {
    errors.push('File name contains invalid path characters');
  }

  // Performance warnings
  if (file.size > 10 * 1024 * 1024) { // 10MB
    warnings.push('Large file size may take longer to upload and process');
  }

  if (file.size > 25 * 1024 * 1024) { // 25MB
    warnings.push('Very large file - consider compressing if possible');
  }
}

function validateSecurity(file: File, errors: string[], warnings: string[], options: SecurityValidationOptions): void {
  const extension = getFileExtension(file.name).toLowerCase();

  // Check for dangerous extensions
  if (DANGEROUS_EXTENSIONS.includes(extension)) {
    errors.push(`File extension "${extension}" is not allowed for security reasons`);
    return;
  }

  // Check for executable files if not allowed
  if (!options.allowExecutables) {
    const executableExtensions = ['.exe', '.msi', '.app', '.deb', '.rpm', '.dmg'];
    if (executableExtensions.includes(extension)) {
      errors.push('Executable files are not allowed');
    }
  }

  // Check for double extensions (e.g., file.txt.exe)
  const nameParts = file.name.split('.');
  if (nameParts.length > 2) {
    const secondToLastExtension = '.' + nameParts[nameParts.length - 2];
    if (DANGEROUS_EXTENSIONS.includes(secondToLastExtension.toLowerCase())) {
      errors.push('Files with suspicious double extensions are not allowed');
    }
  }

  // Check for known malicious patterns in filename
  const suspiciousPatterns = [
    /autorun\.inf/i,
    /desktop\.ini/i,
    /thumbs\.db/i,
    /\.lnk$/i,
    /\.scr$/i
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(file.name)) {
      errors.push('File name matches a suspicious pattern');
      break;
    }
  }

  // Warn about script files that might be allowed but are risky
  const scriptExtensions = ['.html', '.htm', '.svg', '.xml'];
  if (scriptExtensions.includes(extension)) {
    warnings.push('Script files may contain executable content - review carefully');
  }
}

export function validateFileHeaders(file: File): Promise<{ isValid: boolean; errors: string[]; warnings: string[] }> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    const errors: string[] = [];
    const warnings: string[] = [];

    reader.onload = (event) => {
      try {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        if (!arrayBuffer) {
          warnings.push('Could not read file headers');
          resolve({ isValid: true, errors, warnings });
          return;
        }

        const uint8Array = new Uint8Array(arrayBuffer.slice(0, 16)); // Read first 16 bytes
        const isValidHeader = validateFileSignature(uint8Array, file.type);

        if (!isValidHeader && file.type && FILE_SIGNATURES[file.type]) {
          errors.push('File content does not match its declared type');
        }

        // Check for embedded executables in files
        if (containsSuspiciousContent(uint8Array)) {
          warnings.push('File may contain embedded executable content');
        }

        resolve({ isValid: errors.length === 0, errors, warnings });
      } catch (error) {
        warnings.push('Error validating file headers');
        resolve({ isValid: true, errors, warnings });
      }
    };

    reader.onerror = () => {
      warnings.push('Could not read file for header validation');
      resolve({ isValid: true, errors, warnings });
    };

    // Read first 512 bytes for header validation
    const blob = file.slice(0, 512);
    reader.readAsArrayBuffer(blob);
  });
}

function validateFileSignature(bytes: Uint8Array, mimeType: string): boolean {
  if (!mimeType || !FILE_SIGNATURES[mimeType]) {
    return true; // No signature to check
  }

  const signatures = FILE_SIGNATURES[mimeType];
  
  return signatures.some(signature => 
    signature.every((byte, index) => 
      index < bytes.length && bytes[index] === byte
    )
  );
}

function containsSuspiciousContent(bytes: Uint8Array): boolean {
  // Check for MZ header (Windows executable)
  if (bytes.length >= 2 && bytes[0] === 0x4D && bytes[1] === 0x5A) {
    return true;
  }

  // Check for ELF header (Linux executable)
  if (bytes.length >= 4 && 
      bytes[0] === 0x7F && bytes[1] === 0x45 && 
      bytes[2] === 0x4C && bytes[3] === 0x46) {
    return true;
  }

  // Check for Mach-O header (macOS executable)
  if (bytes.length >= 4) {
    const header = (bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
    if (header === 0xFEEDFACE || header === 0xFEEDFACF || 
        header === 0xCEFAEDFE || header === 0xCFFAEDFE) {
      return true;
    }
  }

  return false;
}

function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot === -1 ? '' : filename.substring(lastDot);
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Enhanced validation for specific contexts
export function validateForContext(
  file: File, 
  context: 'support_request' | 'student_record' | 'school_document' | 'general'
): Promise<FileValidationResult> {
  const baseOptions = { ...DEFAULT_SECURITY_OPTIONS };

  switch (context) {
    case 'support_request':
      // More lenient for support requests but still secure
      return validateFile(file, {
        ...baseOptions,
        checkFileHeaders: true
      });
    
    case 'student_record':
      // Stricter validation for student records
      return validateFile(file, {
        ...baseOptions,
        allowExecutables: false,
        scanForMalware: true,
        checkFileHeaders: true
      });
    
    case 'school_document':
      // Balanced validation for school documents
      return validateFile(file, {
        ...baseOptions,
        checkFileHeaders: true
      });
    
    default:
      return validateFile(file, baseOptions);
  }
}

// Batch validation for multiple files
export async function validateFiles(
  files: File[], 
  context: 'support_request' | 'student_record' | 'school_document' | 'general' = 'general'
): Promise<{ file: File; result: FileValidationResult }[]> {
  const results = await Promise.all(
    files.map(async (file) => ({
      file,
      result: await validateForContext(file, context)
    }))
  );

  return results;
}

// Enhanced file name validation with sanitization
export function validateFileName(fileName: string): { isValid: boolean; errors: string[]; sanitizedFileName: string } {
  const errors: string[] = [];
  
  // Comprehensive path traversal check
  const pathTraversalPatterns = [
    /\.\./,                    // Basic traversal
    /\.\.\\/,                  // Windows traversal
    /%2e%2e/i,                 // URL encoded
    /%252e%252e/i,             // Double encoded
    /\x00/,                    // Null byte
    /[\x01-\x1f\x7f-\x9f]/,   // Control characters
    /\/{2,}/,                  // Multiple slashes
    /\\/,                      // Backslash
    /^\.+/,                    // Leading dots
    /~[0-9]/,                  // Tilde expansion
    /\$\{/,                    // Variable expansion
    /`/,                       // Command substitution
  ];
  
  if (pathTraversalPatterns.some(pattern => pattern.test(fileName))) {
    errors.push('File name contains invalid path characters');
  }
  
  // Check for dangerous extensions
  const extension = getFileExtension(fileName).toLowerCase();
  if (DANGEROUS_EXTENSIONS.includes(extension)) {
    errors.push('File extension is not allowed for security reasons');
  }
  
  // Sanitize filename - only allow safe characters
  const sanitized = fileName
    .replace(/[^a-zA-Z0-9._-]/g, '_')  // Only allow alphanumeric, dots, underscores, hyphens
    .replace(/\.{2,}/g, '.')           // Replace multiple dots with single dot
    .replace(/^\.+/, '')               // Remove leading dots
    .replace(/\s+/g, '_')              // Replace spaces with underscores
    .substring(0, 255);                // Limit length
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitizedFileName: sanitized
  };
}

// Enhanced content validation with magic number checking
export async function validateFileContent(file: File): Promise<{ isValid: boolean; errors: string[]; warnings: string[] }> {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  try {
    // Read first 512 bytes for analysis
    const arrayBuffer = await file.slice(0, 512).arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    
    // Check if file content matches declared type
    if (file.type && FILE_SIGNATURES[file.type]) {
      const validSignatures = FILE_SIGNATURES[file.type];
      const hasValidSignature = validSignatures.some(signature =>
        signature.every((byte, index) => index < bytes.length && bytes[index] === byte)
      );
      
      if (!hasValidSignature) {
        errors.push('File content does not match declared type');
      }
    }
    
    // Check for embedded executables or scripts
    const fileText = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    
    // Check for script tags and common XSS patterns
    const dangerousPatterns = [
      /<script[^>]*>/i,
      /javascript:/i,
      /onerror\s*=/i,
      /onclick\s*=/i,
      /onload\s*=/i,
      /<iframe/i,
      /<embed/i,
      /<object/i,
      /eval\s*\(/i,
      /expression\s*\(/i,
      /vbscript:/i,
      /data:text\/html/i,
    ];
    
    if (dangerousPatterns.some(pattern => pattern.test(fileText))) {
      errors.push('File contains potentially malicious content');
    }
    
    // Check for polyglot files (files pretending to be multiple types)
    if (containsSuspiciousContent(bytes) && file.type && file.type.startsWith('image/')) {
      errors.push('Image file contains executable content');
    }
    
    return { isValid: errors.length === 0, errors, warnings };
  } catch (error) {
    warnings.push('Could not fully validate file content');
    return { isValid: errors.length === 0, errors, warnings };
  }
}

// Virus scanning stub for future integration
export async function scanFileForVirus(file: File): Promise<{ isClean: boolean; threats: string[] }> {
  // TODO: Integrate with ClamAV or similar antivirus service
  // For now, implement basic pattern checking
  
  const suspicious = await checkSuspiciousPatterns(file);
  
  return {
    isClean: !suspicious,
    threats: suspicious ? ['Suspicious pattern detected'] : []
  };
}

async function checkSuspiciousPatterns(file: File): Promise<boolean> {
  try {
    const arrayBuffer = await file.slice(0, 1024).arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    const text = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    
    // Check for EICAR test string (standard antivirus test pattern)
    if (text.includes('X5O!P%@AP[4\\PZX54(P^)7CC)7}$EICAR-STANDARD-ANTIVIRUS-TEST-FILE!$H+H*')) {
      return true;
    }
    
    // Check for common malware signatures
    const malwarePatterns = [
      /WScript\.Shell/i,
      /cmd\.exe.*\/c/i,
      /powershell.*-enc/i,
      /base64_decode.*eval/i,
      /document\.write.*unescape/i,
    ];
    
    return malwarePatterns.some(pattern => pattern.test(text));
  } catch {
    return false;
  }
}

// Generate secure file hash for integrity checking
export async function generateFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}